import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { 
  AlertCircle,
  Key,
  Shield,
  Copy,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  Edit3,
  Save,
  X,
  Lock,
  Users,
  Settings,
  ExternalLink
} from 'lucide-react'
import { toast } from 'sonner'
import { serversApi } from '@/lib/api'
import type { ConnectionConfig, SecurityScheme, SecuritySchemeRequest, MCPConfigResponse } from '@/lib/api'
import { OAuth21ConfigurationModal } from './OAuth21ConfigurationModal'

interface AuthInfo {
  server_id: string
  security_schemes: Record<string, SecurityScheme>
  credentials_configured?: string[]
  default_auth?: string[]
  stats?: {
    total_requests: number
    authenticated_requests: number
    failed_auth_attempts: number
  }
}

interface Credential {
  type: string
  value: string
  username?: string
  scopes?: string[]
}

interface OAuth2AuthorizationConfig {
  enabled: boolean
  authorization_server: {
    issuer: string
    authorization_endpoint: string
    token_endpoint: string
    introspection_endpoint: string
    revocation_endpoint: string
    jwks_uri: string
    registration_endpoint: string
    scopes_supported: string[]
    response_types_supported: string[]
    grant_types_supported: string[]
    code_challenge_methods_supported: string[]
    token_endpoint_auth_methods_supported: string[]
  }
  resource_metadata: {
    resource: string
    authorization_servers: string[]
    scopes_supported: string[]
    bearer_methods_supported: string[]
  }
  dynamic_registration: {
    enabled: boolean
    initial_access_token_required: boolean
    registration_endpoint: string
  }
  registered_clients: Array<{
    client_id: string
    client_name: string
    client_uri?: string
    redirect_uris: string[]
    grant_types: string[]
    response_types: string[]
    scopes: string[]
    created_at: string
    status: 'active' | 'disabled' | 'revoked'
  }>
  // TTL fields as string duration format (e.g., "1h", "30m", "24h")
  access_token_ttl?: string
  refresh_token_ttl?: string
  authorization_code_ttl?: string
}

interface AuthenticationTabProps {
  serverId: string
  serverName: string
  transport: string
  port: number
}

export function AuthenticationTab({ serverId, serverName, transport, port }: AuthenticationTabProps) {
  const [authInfo, setAuthInfo] = useState<AuthInfo | null>(null)
  const [connectionConfig, setConnectionConfig] = useState<ConnectionConfig | null>(null)
  const [mcpConfig, setMcpConfig] = useState<MCPConfigResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCredentials, setShowCredentials] = useState<Record<string, boolean>>({})
  const [addingCredential, setAddingCredential] = useState(false)
  const [newCredential, setNewCredential] = useState({
    scheme_name: '',
    type: '',
    value: '',
    username: '',
    scopes: ''
  })

  // Security scheme management state
  const [securitySchemes, setSecuritySchemes] = useState<Record<string, SecurityScheme>>({})
  const [addingSecurityScheme, setAddingSecurityScheme] = useState(false)
  const [editingSecurityScheme, setEditingSecurityScheme] = useState<string | null>(null)
  const [newSecurityScheme, setNewSecurityScheme] = useState({
    name: '',
    type: '',
    scheme: '',
    bearer_format: '',
    in: '',
    parameter_name: '',
    description: ''
  })
  const [editSecurityScheme, setEditSecurityScheme] = useState<SecuritySchemeRequest>({
    type: '',
    scheme: '',
    bearer_format: '',
    in: '',
    name: '',
    description: ''
  })

  // OAuth 2.1 authorization server state
  const [oauthConfig, setOauthConfig] = useState<OAuth2AuthorizationConfig>({
    enabled: false,
    authorization_server: {
      issuer: `http://localhost:${port}`,
      authorization_endpoint: `http://localhost:${port}/oauth/authorize`,
      token_endpoint: `http://localhost:${port}/oauth/token`,
      introspection_endpoint: `http://localhost:${port}/oauth/introspect`,
      revocation_endpoint: `http://localhost:${port}/oauth/revoke`,
      jwks_uri: `http://localhost:${port}/.well-known/jwks.json`,
      registration_endpoint: `http://localhost:${port}/oauth/register`,
      scopes_supported: ['read', 'write', 'admin'],
      response_types_supported: ['code', 'token'],
      grant_types_supported: ['authorization_code', 'client_credentials', 'refresh_token'],
      code_challenge_methods_supported: ['S256'],
      token_endpoint_auth_methods_supported: ['client_secret_basic', 'client_secret_post', 'none']
    },
    resource_metadata: {
      resource: `http://localhost:${port}/mcp`,
      authorization_servers: [`http://localhost:${port}`],
      scopes_supported: ['read', 'write'],
      bearer_methods_supported: ['header', 'body']
    },
    dynamic_registration: {
      enabled: true,
      initial_access_token_required: false,
      registration_endpoint: `http://localhost:${port}/oauth/register`
    },
    registered_clients: [],
    access_token_ttl: '1h',
    refresh_token_ttl: '24h',
    authorization_code_ttl: '10m'
  })
  const [oauthConfigLoading, setOauthConfigLoading] = useState(false)
  const [showOauthConfigModal, setShowOauthConfigModal] = useState(false)
  const [addingOauthClient, setAddingOauthClient] = useState(false)
  const [newOauthClient, setNewOauthClient] = useState({
    client_name: '',
    client_uri: '',
    redirect_uris: '',
    grant_types: ['authorization_code'],
    response_types: ['code'],
    scopes: ['read']
  })

  // Helper functions to convert between nanoseconds and duration strings
  const nanosecondsToDuration = (nanoseconds: number): string => {
    const seconds = nanoseconds / 1000000000;
    
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      return `${hours}h`;
    }
  };
  
  const durationToNanoseconds = (duration: string): number => {
    const match = duration.match(/^(\d+)([smhd])$/);
    if (!match) return 0;
    
    const value = parseInt(match[1]);
    const unit = match[2];
    
    switch (unit) {
      case 's': return value * 1000000000;
      case 'm': return value * 60 * 1000000000;
      case 'h': return value * 3600 * 1000000000;
      case 'd': return value * 86400 * 1000000000;
      default: return 0;
    }
  };
  
  const fetchOauthConfig = async () => {
    try {
      setOauthConfigLoading(true)
      const response = await serversApi.getOauthConfig(serverId)
      if (response) {
        // Convert numeric TTL values to string duration format
        const configWithStringTTLs = {
          ...response,
          access_token_ttl: typeof response.access_token_ttl === 'number' 
            ? nanosecondsToDuration(response.access_token_ttl)
            : response.access_token_ttl || '1h',
          refresh_token_ttl: typeof response.refresh_token_ttl === 'number'
            ? nanosecondsToDuration(response.refresh_token_ttl) 
            : response.refresh_token_ttl || '24h',
          authorization_code_ttl: typeof response.authorization_code_ttl === 'number'
            ? nanosecondsToDuration(response.authorization_code_ttl)
            : response.authorization_code_ttl || '10m'
        };
        setOauthConfig(configWithStringTTLs)
      }
      // If response is null/undefined, keep the default config
    } catch (error) {
      console.error('Failed to fetch OAuth 2.1 configuration:', error)
      // Keep default config if fetch fails - don't change oauthConfig state
    } finally {
      setOauthConfigLoading(false)
    }
  }

  const fetchMCPConfig = async () => {
    console.log('fetchMCPConfig called with serverId:', serverId)
    
    if (!serverId || serverId === 'undefined' || serverId.trim() === '') {
      console.log('fetchMCPConfig: Invalid server ID', serverId)
      return
    }

    try {
      console.log('fetchMCPConfig: Making API call to getMCPConfig with serverId:', serverId)
      
      // Fetch MCP configuration
      const response = await serversApi.getMCPConfig(serverId)
      console.log('fetchMCPConfig: Got response:', response)
      
      // Only set if we got a valid config (not just a message saying no config found)
      if (response && 'config' in response && response.config) {
        setMcpConfig(response as MCPConfigResponse)
      } else {
        setMcpConfig(null)
      }
      
    } catch (err) {
      console.error('Failed to fetch MCP config:', err)
      setMcpConfig(null)
    }
  }

  useEffect(() => {
    console.log('AuthenticationTab useEffect - serverId:', serverId, 'type:', typeof serverId)
    if (serverId && serverId !== '' && serverId !== 'undefined' && serverId.trim() !== '') {
      console.log('Calling fetchAuthInfo, fetchConnectionConfig, fetchSecuritySchemes, fetchOauthConfig, and fetchMCPConfig with serverId:', serverId)
      fetchAuthInfo()
      fetchConnectionConfig()
      fetchSecuritySchemes()
      fetchOauthConfig()
      fetchMCPConfig()
    } else {
      console.log('Not calling fetch functions - invalid serverId:', serverId)
      setLoading(false)
    }
  }, [serverId])

  const fetchAuthInfo = async () => {
    console.log('fetchAuthInfo called with serverId:', serverId)
    
    if (!serverId || serverId === 'undefined' || serverId.trim() === '') {
      console.log('fetchAuthInfo: Invalid server ID', serverId)
      setError('Invalid server ID')
      setLoading(false)
      return
    }

    try {
      console.log('fetchAuthInfo: Making API call to getAuthInfo with serverId:', serverId)
      setLoading(true)
      setError(null)
      
      // Fetch authentication info
      const response = await serversApi.getAuthInfo(serverId)
      console.log('fetchAuthInfo: Got response:', response)
      setAuthInfo(response as AuthInfo)
      
    } catch (err) {
      console.error('Failed to fetch auth info:', err)
      setError('Failed to load authentication information')
    } finally {
      setLoading(false)
    }
  }

  const fetchConnectionConfig = async () => {
    console.log('fetchConnectionConfig called with serverId:', serverId)
    
    if (!serverId || serverId === 'undefined' || serverId.trim() === '') {
      console.log('fetchConnectionConfig: Invalid server ID', serverId)
      return
    }

    try {
      console.log('fetchConnectionConfig: Making API call to getConnectionConfig with serverId:', serverId)
      
      // Fetch connection configuration
      const response = await serversApi.getConnectionConfig(serverId)
      console.log('fetchConnectionConfig: Got response:', response)
      setConnectionConfig(response)
      
    } catch (err) {
      console.error('Failed to fetch connection config:', err)
      // Don't set error for connection config as it's not critical
    }
  }

  const handleAddCredential = async () => {
    if (!serverId || serverId === 'undefined') {
      toast.error('Invalid server ID')
      return
    }

    if (!newCredential.scheme_name || !newCredential.type || !newCredential.value) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      const credentials: Credential = {
        type: newCredential.type,
        value: newCredential.value,
      }

      if (newCredential.username) {
        credentials.username = newCredential.username
      }

      if (newCredential.scopes) {
        credentials.scopes = newCredential.scopes.split(',').map(s => s.trim())
      }

      await serversApi.setCredentials(serverId, {
        scheme_name: newCredential.scheme_name,
        credentials
      })

      toast.success('Credentials added successfully')
      setAddingCredential(false)
      setNewCredential({
        scheme_name: '',
        type: '',
        value: '',
        username: '',
        scopes: ''
      })
      
      // Refresh auth info
      fetchAuthInfo()
    } catch (err) {
      console.error('Failed to add credentials:', err)
      toast.error('Failed to add credentials')
    }
  }

  const handleDeleteCredential = async (schemeName: string) => {
    if (!serverId || serverId === 'undefined') {
      toast.error('Invalid server ID')
      return
    }

    try {
      await serversApi.deleteCredentials(serverId, schemeName)
      toast.success('Credentials deleted successfully')
      fetchAuthInfo()
    } catch (err) {
      console.error('Failed to delete credentials:', err)
      toast.error('Failed to delete credentials')
    }
  }

  const toggleCredentialVisibility = (schemeName: string) => {
    setShowCredentials(prev => ({
      ...prev,
      [schemeName]: !prev[schemeName]
    }))
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const getAuthTypeColor = (type: string) => {
    switch (type) {
      case 'apiKey':
        return 'bg-blue-100 text-blue-800'
      case 'http':
        return 'bg-green-100 text-green-800'
      case 'oauth2':
        return 'bg-purple-100 text-purple-800'
      case 'bearer':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Security scheme management functions
  const fetchSecuritySchemes = async () => {
    if (!serverId || serverId === 'undefined' || serverId.trim() === '') {
      return
    }

    try {
      const response = await serversApi.listSecuritySchemes(serverId)
      setSecuritySchemes(response.security_schemes || {})
    } catch (err) {
      console.error('Failed to fetch security schemes:', err)
      toast.error('Failed to load security schemes')
    }
  }

  const handleCreateSecurityScheme = async () => {
    if (!serverId || serverId === 'undefined') {
      toast.error('Invalid server ID')
      return
    }

    if (!newSecurityScheme.name || !newSecurityScheme.type) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      const schemeData: SecuritySchemeRequest = {
        type: newSecurityScheme.type,
        scheme: newSecurityScheme.scheme,
        bearer_format: newSecurityScheme.bearer_format,
        in: newSecurityScheme.in,
        name: newSecurityScheme.parameter_name,
        description: newSecurityScheme.description
      }

      await serversApi.createSecurityScheme(serverId, newSecurityScheme.name, schemeData)
      toast.success('Security scheme created successfully')
      setAddingSecurityScheme(false)
      setNewSecurityScheme({
        name: '',
        type: '',
        scheme: '',
        bearer_format: '',
        in: '',
        parameter_name: '',
        description: ''
      })
      
      // Refresh security schemes and auth info
      await fetchSecuritySchemes()
      await fetchAuthInfo()
    } catch (err) {
      console.error('Failed to create security scheme:', err)
      toast.error('Failed to create security scheme')
    }
  }

  const handleUpdateSecurityScheme = async (schemeName: string) => {
    if (!serverId || serverId === 'undefined') {
      toast.error('Invalid server ID')
      return
    }

    if (!editSecurityScheme.type) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await serversApi.updateSecurityScheme(serverId, schemeName, editSecurityScheme)
      toast.success('Security scheme updated successfully')
      setEditingSecurityScheme(null)
      setEditSecurityScheme({
        type: '',
        scheme: '',
        bearer_format: '',
        in: '',
        name: '',
        description: ''
      })
      
      // Refresh security schemes and auth info
      await fetchSecuritySchemes()
      await fetchAuthInfo()
    } catch (err) {
      console.error('Failed to update security scheme:', err)
      toast.error('Failed to update security scheme')
    }
  }

  const handleDeleteSecurityScheme = async (schemeName: string) => {
    if (!serverId || serverId === 'undefined') {
      toast.error('Invalid server ID')
      return
    }

    if (!confirm(`Are you sure you want to delete the security scheme "${schemeName}"? This will also delete any associated credentials.`)) {
      return
    }

    try {
      await serversApi.deleteSecurityScheme(serverId, schemeName)
      toast.success('Security scheme deleted successfully')
      
      // Refresh security schemes and auth info
      await fetchSecuritySchemes()
      await fetchAuthInfo()
    } catch (err) {
      console.error('Failed to delete security scheme:', err)
      toast.error('Failed to delete security scheme')
    }
  }

  const startEditingSecurityScheme = (schemeName: string, scheme: SecurityScheme) => {
    setEditingSecurityScheme(schemeName)
    setEditSecurityScheme({
      type: scheme.type,
      scheme: scheme.scheme || '',
      bearer_format: scheme.bearer_format || '',
      in: scheme.in || '',
      name: scheme.name || '',
      description: scheme.description || ''
    })
  }

  const cancelEditingSecurityScheme = () => {
    setEditingSecurityScheme(null)
    setEditSecurityScheme({
      type: '',
      scheme: '',
      bearer_format: '',
      in: '',
      name: '',
      description: ''
    })
  }

  // OAuth 2.1 authorization server functions
  
  const handleToggleOauth = async (enabled: boolean) => {
    try {
      setOauthConfigLoading(true)
      const newConfig = { ...oauthConfig, enabled }
      await serversApi.updateOauthConfig(serverId, newConfig)
      setOauthConfig(newConfig)
      toast.success(enabled ? 'OAuth 2.1 authorization server enabled' : 'OAuth 2.1 authorization server disabled')
    } catch (error) {
      toast.error('Failed to update OAuth 2.1 configuration')
      console.error('Failed to update OAuth 2.1 configuration:', error)
    } finally {
      setOauthConfigLoading(false)
    }
  }

  const handleAddOauthClient = async () => {
    try {
      const clientData = {
        client_name: newOauthClient.client_name,
        client_uri: newOauthClient.client_uri || undefined,
        redirect_uris: newOauthClient.redirect_uris.split('\n').filter(uri => uri.trim()),
        grant_types: newOauthClient.grant_types,
        response_types: newOauthClient.response_types,
        scopes: newOauthClient.scopes
      }

      const registeredClient = await serversApi.registerOauthClient(serverId, clientData)
      
      setOauthConfig(prev => ({
        ...prev,
        registered_clients: [...prev.registered_clients, registeredClient]
      }))
      
      setNewOauthClient({
        client_name: '',
        client_uri: '',
        redirect_uris: '',
        grant_types: ['authorization_code'],
        response_types: ['code'],
        scopes: ['read']
      })
      setAddingOauthClient(false)
      toast.success('OAuth 2.1 client registered successfully')
    } catch (error) {
      toast.error('Failed to register OAuth 2.1 client')
      console.error('Failed to register OAuth 2.1 client:', error)
    }
  }

  const handleDeleteOauthClient = async (clientId: string) => {
    try {
      await serversApi.deleteOauthClient(serverId, clientId)
      setOauthConfig(prev => ({
        ...prev,
        registered_clients: prev.registered_clients.filter(client => client.client_id !== clientId)
      }))
      toast.success('OAuth 2.1 client deleted successfully')
    } catch (error) {
      toast.error('Failed to delete OAuth 2.1 client')
      console.error('Failed to delete OAuth 2.1 client:', error)
    }
  }

  const handleToggleOauthClient = async (clientId: string, status: 'active' | 'disabled') => {
    try {
      await serversApi.updateOauthClientStatus(serverId, clientId, status)
      setOauthConfig(prev => ({
        ...prev,
        registered_clients: prev.registered_clients.map(client => 
          client.client_id === clientId ? { ...client, status } : client
        )
      }))
      toast.success(`OAuth 2.1 client ${status === 'active' ? 'enabled' : 'disabled'} successfully`)
    } catch (error) {
      toast.error('Failed to update OAuth 2.1 client status')
      console.error('Failed to update OAuth 2.1 client status:', error)
    }
  }

  // Show waiting message if no server ID yet
  if (!serverId || serverId === '' || serverId === 'undefined') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-yellow-600">
            <AlertCircle className="mr-2 h-5 w-5" />
            Waiting for Server Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Waiting for server information to load before fetching authentication details...
          </p>
        </CardContent>
      </Card>
    )
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-16 bg-gray-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-red-600">
            <AlertCircle className="mr-2 h-5 w-5" />
            Error Loading Authentication
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={fetchAuthInfo} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Connection Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Shield className="mr-2 h-5 w-5" />
              Connection Configuration
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchConnectionConfig}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </CardTitle>
          <CardDescription>
            Configuration details for connecting external MCP clients
          </CardDescription>
        </CardHeader>
        <CardContent>
          {connectionConfig ? (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-sm font-medium">Server Name</Label>
                  <div className="flex items-center space-x-2">
                    <Input value={connectionConfig.server_name} readOnly />
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => copyToClipboard(connectionConfig.server_name)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Transport</Label>
                  <div className="flex items-center space-x-2">
                    <Input value={connectionConfig.transport} readOnly />
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => copyToClipboard(connectionConfig.transport)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="md:col-span-2">
                  <Label className="text-sm font-medium">MCP Endpoint</Label>
                  <div className="flex items-center space-x-2">
                    <Input value={connectionConfig.mcp_endpoint} readOnly />
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => copyToClipboard(connectionConfig.mcp_endpoint)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* OAuth 2.1 Endpoints */}
              {oauthConfig.enabled && (
                <div>
                  <Label className="text-sm font-medium">OAuth 2.1 Endpoints</Label>
                  <div className="grid gap-4 md:grid-cols-2 mt-2">
                    <div>
                      <Label className="text-xs text-gray-500">Authorization</Label>
                      <div className="flex items-center space-x-2">
                        <Input value={oauthConfig?.authorization_server?.authorization_endpoint || ''} readOnly className="text-sm" />
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(oauthConfig?.authorization_server?.authorization_endpoint || '')}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs text-gray-500">Token</Label>
                      <div className="flex items-center space-x-2">
                        <Input value={oauthConfig?.authorization_server?.token_endpoint || ''} readOnly className="text-sm" />
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(oauthConfig?.authorization_server?.token_endpoint || '')}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs text-gray-500">JWKS</Label>
                      <div className="flex items-center space-x-2">
                        <Input value={oauthConfig?.authorization_server?.jwks_uri || ''} readOnly className="text-sm" />
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(oauthConfig?.authorization_server?.jwks_uri || '')}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs text-gray-500">Registration</Label>
                      <div className="flex items-center space-x-2">
                        <Input value={oauthConfig?.authorization_server?.registration_endpoint || ''} readOnly className="text-sm" />
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(oauthConfig?.authorization_server?.registration_endpoint || '')}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Well-known endpoints */}
              {oauthConfig.enabled && (
                <div>
                  <Label className="text-sm font-medium">Well-known Discovery Endpoints</Label>
                  <div className="grid gap-2 mt-2">
                    <div>
                      <Label className="text-xs text-gray-500">Authorization Server Metadata</Label>
                      <div className="flex items-center space-x-2">
                        <Input value={`${oauthConfig?.authorization_server?.issuer || ''}/.well-known/oauth-authorization-server`} readOnly className="text-sm" />
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(`${oauthConfig?.authorization_server?.issuer || ''}/.well-known/oauth-authorization-server`)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs text-gray-500">Protected Resource Metadata</Label>
                      <div className="flex items-center space-x-2">
                        <Input value={`${oauthConfig?.authorization_server?.issuer || ''}/.well-known/oauth-protected-resource`} readOnly className="text-sm" />
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(`${oauthConfig?.authorization_server?.issuer || ''}/.well-known/oauth-protected-resource`)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-sm font-medium">Full Configuration (JSON)</Label>
                <div className="mt-2">
                  <pre className="bg-gray-50 p-4 rounded-md text-sm overflow-x-auto">
                    {JSON.stringify(connectionConfig, null, 2)}
                  </pre>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="mt-2"
                    onClick={() => copyToClipboard(JSON.stringify(connectionConfig, null, 2))}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Configuration
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2 mx-auto"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
              </div>
              <p className="text-muted-foreground mt-4">Loading connection configuration...</p>
            </div>
          )}
        </CardContent>
      </Card>

             {/* OAuth 2.1 Authorization Server Configuration */}
       <Card>
         <CardHeader>
           <CardTitle className="flex items-center justify-between">
             <div className="flex items-center">
               <Lock className="mr-2 h-5 w-5" />
               OAuth 2.1 Authorization Server
             </div>
             <div className="flex items-center space-x-2">
               <Button
                 size="sm"
                 variant="outline"
                 onClick={fetchOauthConfig}
                 disabled={oauthConfigLoading}
               >
                 <RefreshCw className="mr-2 h-4 w-4" />
                 Refresh
               </Button>
               <Button
                 size="sm"
                 variant="outline"
                 onClick={() => setShowOauthConfigModal(true)}
               >
                 <Settings className="mr-2 h-4 w-4" />
                 Configure
               </Button>
             </div>
           </CardTitle>
           <CardDescription>
             Enable OAuth 2.1 authorization server for MCP client authentication per the MCP specification.
           </CardDescription>
         </CardHeader>
         <CardContent>
           <div className="flex items-center justify-between">
             <div className="flex items-center space-x-2">
               <Lock className="h-6 w-6 text-blue-500" />
               <div>
                 <span className="font-medium">OAuth 2.1 Authorization Server</span>
                 <p className="text-sm text-gray-600">
                   Compliant with MCP specification draft/basic/authorization
                 </p>
               </div>
             </div>
             <Switch
               id="oauth2-1-enabled"
               checked={oauthConfig.enabled}
               onCheckedChange={handleToggleOauth}
               disabled={oauthConfigLoading}
             />
           </div>
           
           {oauthConfig.enabled && (
             <div className="mt-6 space-y-4">
               <div className="grid gap-4 md:grid-cols-2">
                 <div>
                   <Label className="text-sm font-medium">Authorization Endpoint</Label>
                   <div className="flex items-center space-x-2">
                     <Input value={oauthConfig?.authorization_server?.authorization_endpoint || ''} readOnly />
                     <Button 
                       size="sm" 
                       variant="outline"
                       onClick={() => copyToClipboard(oauthConfig?.authorization_server?.authorization_endpoint || '')}
                     >
                       <Copy className="h-4 w-4" />
                     </Button>
                   </div>
                 </div>
                 
                 <div>
                   <Label className="text-sm font-medium">Token Endpoint</Label>
                   <div className="flex items-center space-x-2">
                     <Input value={oauthConfig?.authorization_server?.token_endpoint || ''} readOnly />
                     <Button 
                       size="sm" 
                       variant="outline"
                       onClick={() => copyToClipboard(oauthConfig?.authorization_server?.token_endpoint || '')}
                     >
                       <Copy className="h-4 w-4" />
                     </Button>
                   </div>
                 </div>
                 
                 <div>
                   <Label className="text-sm font-medium">JWKS URI</Label>
                   <div className="flex items-center space-x-2">
                     <Input value={oauthConfig?.authorization_server?.jwks_uri || ''} readOnly />
                     <Button 
                       size="sm" 
                       variant="outline"
                       onClick={() => copyToClipboard(oauthConfig?.authorization_server?.jwks_uri || '')}
                     >
                       <Copy className="h-4 w-4" />
                     </Button>
                   </div>
                 </div>
                 
                 <div>
                   <Label className="text-sm font-medium">Registration Endpoint</Label>
                   <div className="flex items-center space-x-2">
                     <Input value={oauthConfig?.authorization_server?.registration_endpoint || ''} readOnly />
                     <Button 
                       size="sm" 
                       variant="outline"
                       onClick={() => copyToClipboard(oauthConfig?.authorization_server?.registration_endpoint || '')}
                     >
                       <Copy className="h-4 w-4" />
                     </Button>
                   </div>
                 </div>
               </div>
               
               <div>
                 <Label className="text-sm font-medium">Supported Scopes</Label>
                 <div className="flex flex-wrap gap-2 mt-2">
                   {oauthConfig?.authorization_server?.scopes_supported?.map((scope) => (
                     <Badge key={scope} variant="secondary">{scope}</Badge>
                   )) || []}
                 </div>
               </div>
               
               <div>
                 <Label className="text-sm font-medium">Supported Grant Types</Label>
                 <div className="flex flex-wrap gap-2 mt-2">
                   {oauthConfig?.authorization_server?.grant_types_supported?.map((grantType) => (
                     <Badge key={grantType} variant="outline">{grantType}</Badge>
                   )) || []}
                 </div>
               </div>
               
               {/* TTL Configuration */}
               <div>
                 <Label className="text-sm font-medium mb-3 block">Token Time-to-Live (TTL) Configuration</Label>
                 <div className="grid gap-4 md:grid-cols-3">
                   <div>
                     <Label className="text-sm">Access Token TTL</Label>
                     <Input
                       value={oauthConfig.access_token_ttl || '1h'}
                       onChange={(e) => setOauthConfig(prev => ({ ...prev, access_token_ttl: e.target.value }))}
                       placeholder="e.g., 1h, 30m"
                       className="mt-1"
                     />
                     <p className="text-xs text-gray-500 mt-1">Duration format: 1h, 30m, 24h</p>
                   </div>
                   
                   <div>
                     <Label className="text-sm">Refresh Token TTL</Label>
                     <Input
                       value={oauthConfig.refresh_token_ttl || '24h'}
                       onChange={(e) => setOauthConfig(prev => ({ ...prev, refresh_token_ttl: e.target.value }))}
                       placeholder="e.g., 24h, 720h"
                       className="mt-1"
                     />
                     <p className="text-xs text-gray-500 mt-1">Duration format: 24h, 720h (30 days)</p>
                   </div>
                   
                   <div>
                     <Label className="text-sm">Authorization Code TTL</Label>
                     <Input
                       value={oauthConfig.authorization_code_ttl || '10m'}
                       onChange={(e) => setOauthConfig(prev => ({ ...prev, authorization_code_ttl: e.target.value }))}
                       placeholder="e.g., 10m, 5m"
                       className="mt-1"
                     />
                     <p className="text-xs text-gray-500 mt-1">Duration format: 10m, 5m</p>
                   </div>
                 </div>
                 
                 <div className="mt-3">
                   <Button
                     size="sm"
                     onClick={async () => {
                       try {
                         setOauthConfigLoading(true)
                         await serversApi.updateOauthConfig(serverId, oauthConfig)
                         toast.success('OAuth 2.1 TTL configuration updated successfully')
                       } catch (error) {
                         toast.error('Failed to update OAuth 2.1 TTL configuration')
                         console.error('Failed to update OAuth 2.1 TTL configuration:', error)
                       } finally {
                         setOauthConfigLoading(false)
                       }
                     }}
                     disabled={oauthConfigLoading}
                   >
                     <Save className="mr-2 h-4 w-4" />
                     Update TTL Settings
                   </Button>
                 </div>
               </div>
               
               <div className="flex items-center space-x-2">
                 <Badge variant="default" className="bg-green-100 text-green-800">
                   PKCE Enabled
                 </Badge>
                 <Badge variant="default" className="bg-blue-100 text-blue-800">
                   Dynamic Registration {oauthConfig.dynamic_registration.enabled ? 'Enabled' : 'Disabled'}
                 </Badge>
               </div>
             </div>
           )}
           
           <div className="mt-4 text-sm text-gray-600">
             <p>
               When enabled, this server implements OAuth 2.1 authorization per the MCP specification,
               allowing external MCP clients to authenticate using standard OAuth 2.1 flows with PKCE.
             </p>
             <div className="flex items-center space-x-4 mt-2">
               <a href="https://modelcontextprotocol.io/specification/draft/basic/authorization" target="_blank" rel="noopener noreferrer" className="inline-flex items-center underline">
                 <ExternalLink className="h-4 w-4 mr-1" />
                 MCP Authorization Specification
               </a>
               <a href="https://datatracker.ietf.org/doc/html/draft-ietf-oauth-security-topics" target="_blank" rel="noopener noreferrer" className="inline-flex items-center underline">
                 <ExternalLink className="h-4 w-4 mr-1" />
                 OAuth 2.1 Security Best Practices
               </a>
             </div>
           </div>
                  </CardContent>
       </Card>

       {/* OAuth 2.1 Client Registration */}
       {oauthConfig.enabled && (
         <Card>
           <CardHeader>
             <CardTitle className="flex items-center justify-between">
               <div className="flex items-center">
                 <Users className="mr-2 h-5 w-5" />
                 OAuth 2.1 Client Registration
               </div>
               <Button
                 size="sm"
                 onClick={() => setAddingOauthClient(true)}
                 disabled={oauthConfigLoading}
               >
                 <Plus className="mr-2 h-4 w-4" />
                 Register Client
               </Button>
             </CardTitle>
             <CardDescription>
               Manage OAuth 2.1 client registrations for MCP client authentication.
             </CardDescription>
           </CardHeader>
           <CardContent>
             {/* Add OAuth Client Form */}
             {addingOauthClient && (
               <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                 <h4 className="font-medium mb-4">Register New OAuth 2.1 Client</h4>
                 <div className="space-y-4">
                   <div className="grid gap-4 md:grid-cols-2">
                     <div>
                       <Label>Client Name *</Label>
                       <Input
                         value={newOauthClient.client_name}
                         onChange={(e) => setNewOauthClient(prev => ({ ...prev, client_name: e.target.value }))}
                         placeholder="e.g., My MCP Client"
                       />
                     </div>
                     
                     <div>
                       <Label>Client URI</Label>
                       <Input
                         value={newOauthClient.client_uri}
                         onChange={(e) => setNewOauthClient(prev => ({ ...prev, client_uri: e.target.value }))}
                         placeholder="https://example.com/client"
                       />
                     </div>
                   </div>
                   
                   <div>
                     <Label>Redirect URIs *</Label>
                     <Textarea
                       value={newOauthClient.redirect_uris}
                       onChange={(e) => setNewOauthClient(prev => ({ ...prev, redirect_uris: e.target.value }))}
                       placeholder="https://example.com/callback&#10;https://example.com/oauth/callback"
                       rows={3}
                     />
                     <p className="text-sm text-gray-600 mt-1">Enter one URI per line</p>
                   </div>
                   
                   <div className="grid gap-4 md:grid-cols-2">
                     <div>
                       <Label>Grant Types</Label>
                       <div className="flex flex-wrap gap-2 mt-2">
                         {['authorization_code', 'client_credentials', 'refresh_token'].map((grantType) => (
                           <Badge
                             key={grantType}
                             variant={newOauthClient.grant_types.includes(grantType) ? "default" : "outline"}
                             className="cursor-pointer"
                             onClick={() => {
                               setNewOauthClient(prev => ({
                                 ...prev,
                                 grant_types: prev.grant_types.includes(grantType)
                                   ? prev.grant_types.filter(g => g !== grantType)
                                   : [...prev.grant_types, grantType]
                               }))
                             }}
                           >
                             {grantType}
                           </Badge>
                         ))}
                       </div>
                     </div>
                     
                     <div>
                       <Label>Response Types</Label>
                       <div className="flex flex-wrap gap-2 mt-2">
                         {['code', 'token'].map((responseType) => (
                           <Badge
                             key={responseType}
                             variant={newOauthClient.response_types.includes(responseType) ? "default" : "outline"}
                             className="cursor-pointer"
                             onClick={() => {
                               setNewOauthClient(prev => ({
                                 ...prev,
                                 response_types: prev.response_types.includes(responseType)
                                   ? prev.response_types.filter(r => r !== responseType)
                                   : [...prev.response_types, responseType]
                               }))
                             }}
                           >
                             {responseType}
                           </Badge>
                         ))}
                       </div>
                     </div>
                   </div>
                   
                   <div>
                     <Label>Scopes</Label>
                     <div className="flex flex-wrap gap-2 mt-2">
                       {oauthConfig?.authorization_server?.scopes_supported?.map((scope) => (
                         <Badge
                           key={scope}
                           variant={newOauthClient.scopes.includes(scope) ? "default" : "outline"}
                           className="cursor-pointer"
                           onClick={() => {
                             setNewOauthClient(prev => ({
                               ...prev,
                               scopes: prev.scopes.includes(scope)
                                 ? prev.scopes.filter(s => s !== scope)
                                 : [...prev.scopes, scope]
                             }))
                           }}
                         >
                           {scope}
                         </Badge>
                       )) || []}
                     </div>
                   </div>
                   
                   <div className="flex items-center space-x-2">
                     <Button onClick={handleAddOauthClient} disabled={!newOauthClient.client_name || !newOauthClient.redirect_uris}>
                       <Plus className="mr-2 h-4 w-4" />
                       Register Client
                     </Button>
                     <Button variant="outline" onClick={() => setAddingOauthClient(false)}>
                       <X className="mr-2 h-4 w-4" />
                       Cancel
                     </Button>
                   </div>
                 </div>
               </div>
             )}
             
             {/* Registered Clients */}
             {oauthConfig?.registered_clients?.length > 0 ? (
               <div className="space-y-4">
                 <h4 className="font-medium">Registered Clients</h4>
                 {oauthConfig?.registered_clients?.map((client) => (
                   <div key={client.client_id} className="border rounded-lg p-4">
                     <div className="flex items-center justify-between">
                       <div className="flex items-center space-x-2">
                         <h5 className="font-medium">{client.client_name}</h5>
                         <Badge 
                           variant={client.status === 'active' ? "default" : client.status === 'disabled' ? "secondary" : "destructive"}
                         >
                           {client.status}
                         </Badge>
                       </div>
                       
                       <div className="flex items-center space-x-2">
                         <Button
                           size="sm"
                           variant="outline"
                           onClick={() => handleToggleOauthClient(client.client_id, client.status === 'active' ? 'disabled' : 'active')}
                         >
                           {client.status === 'active' ? 'Disable' : 'Enable'}
                         </Button>
                         <Button
                           size="sm"
                           variant="outline"
                           onClick={() => handleDeleteOauthClient(client.client_id)}
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       </div>
                     </div>
                     
                     <div className="mt-3 text-sm text-gray-600 space-y-2">
                       <div>
                         <span className="font-medium">Client ID:</span> {client.client_id}
                         <Button 
                           size="sm" 
                           variant="outline" 
                           className="ml-2"
                           onClick={() => copyToClipboard(client.client_id)}
                         >
                           <Copy className="h-4 w-4" />
                         </Button>
                       </div>
                       
                       {client.client_uri && (
                         <div>
                           <span className="font-medium">Client URI:</span> {client.client_uri}
                         </div>
                       )}
                       
                       <div>
                         <span className="font-medium">Grant Types:</span> {client.grant_types.join(', ')}
                       </div>
                       
                       <div>
                         <span className="font-medium">Scopes:</span> {client.scopes.join(', ')}
                       </div>
                       
                       <div>
                         <span className="font-medium">Created:</span> {new Date(client.created_at).toLocaleDateString()}
                       </div>
                     </div>
                   </div>
                 ))}
               </div>
             ) : (
               <div className="text-center py-8 text-gray-500">
                 <Users className="h-8 w-8 mx-auto mb-2" />
                 <p>No OAuth 2.1 clients registered yet.</p>
                 <p className="text-sm">Register your first client to enable OAuth 2.1 authentication.</p>
               </div>
             )}
           </CardContent>
         </Card>
       )}

       {/* Security Schemes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Key className="mr-2 h-5 w-5" />
              Security Schemes
            </div>
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  fetchSecuritySchemes()
                  fetchAuthInfo()
                }}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => setAddingSecurityScheme(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Scheme
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            Manage authentication schemes for this server
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Add Security Scheme Form */}
          {addingSecurityScheme && (
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
              <h4 className="font-medium mb-4">Add New Security Scheme</h4>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Scheme Name *</Label>
                    <Input
                      value={newSecurityScheme.name}
                      onChange={(e) => setNewSecurityScheme(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., api_key, bearer_auth"
                    />
                  </div>
                  <div>
                    <Label>Type *</Label>
                    <Select
                      value={newSecurityScheme.type}
                      onValueChange={(value) => setNewSecurityScheme(prev => ({ ...prev, type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="apiKey">API Key</SelectItem>
                        <SelectItem value="http">HTTP</SelectItem>
                        <SelectItem value="oauth2">OAuth2</SelectItem>
                        <SelectItem value="bearer">Bearer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {newSecurityScheme.type === 'http' && (
                  <div>
                    <Label>HTTP Scheme</Label>
                    <Select
                      value={newSecurityScheme.scheme}
                      onValueChange={(value) => setNewSecurityScheme(prev => ({ ...prev, scheme: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select HTTP scheme" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="bearer">Bearer</SelectItem>
                        <SelectItem value="digest">Digest</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {newSecurityScheme.type === 'apiKey' && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Location</Label>
                      <Select
                        value={newSecurityScheme.in}
                        onValueChange={(value) => setNewSecurityScheme(prev => ({ ...prev, in: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="header">Header</SelectItem>
                          <SelectItem value="query">Query Parameter</SelectItem>
                          <SelectItem value="cookie">Cookie</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Parameter Name</Label>
                      <Input
                        value={newSecurityScheme.parameter_name}
                        onChange={(e) => setNewSecurityScheme(prev => ({ ...prev, parameter_name: e.target.value }))}
                        placeholder="e.g., X-API-Key, api_key"
                      />
                    </div>
                  </div>
                )}

                {(newSecurityScheme.type === 'bearer' || newSecurityScheme.scheme === 'bearer') && (
                  <div>
                    <Label>Bearer Format</Label>
                    <Input
                      value={newSecurityScheme.bearer_format}
                      onChange={(e) => setNewSecurityScheme(prev => ({ ...prev, bearer_format: e.target.value }))}
                      placeholder="e.g., JWT, opaque"
                    />
                  </div>
                )}

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newSecurityScheme.description}
                    onChange={(e) => setNewSecurityScheme(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional description for this security scheme"
                    rows={3}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Button onClick={handleCreateSecurityScheme}>
                    <Save className="mr-2 h-4 w-4" />
                    Create Scheme
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setAddingSecurityScheme(false)
                      setNewSecurityScheme({
                        name: '',
                        type: '',
                        scheme: '',
                        bearer_format: '',
                        in: '',
                        parameter_name: '',
                        description: ''
                      })
                    }}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Security Schemes List */}
          <div className="space-y-4">
            {Object.keys(authInfo?.security_schemes || {}).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Key className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-lg font-medium">No security schemes configured</p>
                <p className="text-sm">Add a security scheme to enable authentication for this server</p>
              </div>
            )}

            {Object.entries(authInfo?.security_schemes || {}).map(([name, scheme]) => (
              <div key={name} className="border rounded-lg p-4">
                {editingSecurityScheme === name ? (
                  // Edit mode
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium">Edit Security Scheme: {name}</h4>
                      <Badge className={getAuthTypeColor(scheme.type)}>
                        {scheme.type}
                      </Badge>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label>Type *</Label>
                        <Select
                          value={editSecurityScheme.type}
                          onValueChange={(value) => setEditSecurityScheme(prev => ({ ...prev, type: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="apiKey">API Key</SelectItem>
                            <SelectItem value="http">HTTP</SelectItem>
                            <SelectItem value="oauth2">OAuth2</SelectItem>
                            <SelectItem value="bearer">Bearer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {editSecurityScheme.type === 'http' && (
                        <div>
                          <Label>HTTP Scheme</Label>
                          <Select
                            value={editSecurityScheme.scheme}
                            onValueChange={(value) => setEditSecurityScheme(prev => ({ ...prev, scheme: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select HTTP scheme" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="basic">Basic</SelectItem>
                              <SelectItem value="bearer">Bearer</SelectItem>
                              <SelectItem value="digest">Digest</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {editSecurityScheme.type === 'apiKey' && (
                        <>
                          <div>
                            <Label>Location</Label>
                            <Select
                              value={editSecurityScheme.in}
                              onValueChange={(value) => setEditSecurityScheme(prev => ({ ...prev, in: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select location" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="header">Header</SelectItem>
                                <SelectItem value="query">Query Parameter</SelectItem>
                                <SelectItem value="cookie">Cookie</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Parameter Name</Label>
                            <Input
                              value={editSecurityScheme.name}
                              onChange={(e) => setEditSecurityScheme(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="e.g., X-API-Key, api_key"
                            />
                          </div>
                        </>
                      )}

                      {(editSecurityScheme.type === 'bearer' || editSecurityScheme.scheme === 'bearer') && (
                        <div>
                          <Label>Bearer Format</Label>
                          <Input
                            value={editSecurityScheme.bearer_format}
                            onChange={(e) => setEditSecurityScheme(prev => ({ ...prev, bearer_format: e.target.value }))}
                            placeholder="e.g., JWT, opaque"
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={editSecurityScheme.description}
                        onChange={(e) => setEditSecurityScheme(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Optional description for this security scheme"
                        rows={3}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button onClick={() => handleUpdateSecurityScheme(name)}>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </Button>
                      <Button variant="outline" onClick={cancelEditingSecurityScheme}>
                        <X className="mr-2 h-4 w-4" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View mode
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{name}</h4>
                      <div className="flex items-center space-x-2">
                        <Badge className={getAuthTypeColor(scheme.type)}>
                          {scheme.type}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEditingSecurityScheme(name, scheme)}
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteSecurityScheme(name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      {scheme.description && (
                        <p>{scheme.description}</p>
                      )}
                      
                      <div className="grid gap-2 md:grid-cols-2">
                        {scheme.scheme && (
                          <div>
                            <span className="font-medium">Scheme:</span> {scheme.scheme}
                          </div>
                        )}
                        {scheme.in && (
                          <div>
                            <span className="font-medium">Location:</span> {scheme.in}
                          </div>
                        )}
                        {scheme.name && (
                          <div>
                            <span className="font-medium">Parameter:</span> {scheme.name}
                          </div>
                        )}
                        {scheme.bearer_format && (
                          <div>
                            <span className="font-medium">Format:</span> {scheme.bearer_format}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-2 flex items-center space-x-2">
                      <Badge variant={(authInfo?.credentials_configured || []).includes(name) ? "default" : "outline"}>
                        {(authInfo?.credentials_configured || []).includes(name) ? "Configured" : "Not Configured"}
                      </Badge>
                      
                      {(authInfo?.default_auth || []).includes(name) && (
                        <Badge variant="secondary">Default</Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Configured Credentials */}
      {authInfo?.credentials_configured && authInfo.credentials_configured.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Configured Credentials</CardTitle>
            <CardDescription>
              Currently configured authentication credentials
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {authInfo.credentials_configured.map((schemeName) => (
                <div key={schemeName} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium">{schemeName}</h4>
                      <Badge className={getAuthTypeColor(authInfo.security_schemes[schemeName]?.type || 'unknown')}>
                        {authInfo.security_schemes[schemeName]?.type || 'Unknown'}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleCredentialVisibility(schemeName)}
                      >
                        {showCredentials[schemeName] ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteCredential(schemeName)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {showCredentials[schemeName] && (
                    <div className="mt-3 text-sm text-gray-600">
                      <p>Credential details are hidden for security</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add New Credential */}
      <Card>
        <CardHeader>
          <CardTitle>Add Authentication Credential</CardTitle>
          <CardDescription>
            Configure authentication credentials for this server
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!addingCredential ? (
            <Button onClick={() => setAddingCredential(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Credential
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Scheme Name</Label>
                  <Select 
                    value={newCredential.scheme_name} 
                    onValueChange={(value) => setNewCredential(prev => ({ ...prev, scheme_name: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select scheme" />
                    </SelectTrigger>
                    <SelectContent>
                      {authInfo?.security_schemes && Object.keys(authInfo.security_schemes).map((name) => (
                        <SelectItem key={name} value={name}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Type</Label>
                  <Select 
                    value={newCredential.type} 
                    onValueChange={(value) => setNewCredential(prev => ({ ...prev, type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apiKey">API Key</SelectItem>
                      <SelectItem value="http">HTTP Basic</SelectItem>
                      <SelectItem value="bearer">Bearer Token</SelectItem>
                      <SelectItem value="oauth2">OAuth2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Value</Label>
                <Input
                  type="password"
                  value={newCredential.value}
                  onChange={(e) => setNewCredential(prev => ({ ...prev, value: e.target.value }))}
                  placeholder="Enter credential value"
                />
              </div>
              
              {newCredential.type === 'http' && (
                <div>
                  <Label>Username</Label>
                  <Input
                    value={newCredential.username}
                    onChange={(e) => setNewCredential(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="Enter username"
                  />
                </div>
              )}
              
              {newCredential.type === 'oauth2' && (
                <div>
                  <Label>Scopes (comma-separated)</Label>
                  <Input
                    value={newCredential.scopes}
                    onChange={(e) => setNewCredential(prev => ({ ...prev, scopes: e.target.value }))}
                    placeholder="read,write,admin"
                  />
                </div>
              )}
              
              <div className="flex items-center space-x-2">
                <Button onClick={handleAddCredential}>
                  Add Credential
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setAddingCredential(false)
                    setNewCredential({
                      scheme_name: '',
                      type: '',
                      value: '',
                      username: '',
                      scopes: ''
                    })
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Authentication Statistics */}
      {authInfo?.stats && (
        <Card>
          <CardHeader>
            <CardTitle>Authentication Statistics</CardTitle>
            <CardDescription>
              Usage statistics for authentication
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="text-center">
                <div className="text-2xl font-bold">{authInfo.stats.total_requests}</div>
                <div className="text-sm text-gray-600">Total Requests</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{authInfo.stats.authenticated_requests}</div>
                <div className="text-sm text-gray-600">Authenticated</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{authInfo.stats.failed_auth_attempts}</div>
                <div className="text-sm text-gray-600">Failed Attempts</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* OAuth 2.1 Configuration Modal */}
      <OAuth21ConfigurationModal
        open={showOauthConfigModal}
        onOpenChange={setShowOauthConfigModal}
        serverId={serverId}
        serverName={serverName}
        port={port}
        initialConfig={oauthConfig}
        onConfigUpdate={(updatedConfig) => {
          setOauthConfig(updatedConfig)
          fetchOauthConfig() // Refresh the configuration after update
        }}
      />
    </div>
  )
} 